/**
 * The editable line items on a client quote or invoice page, and the maths
 * that turns them into a subtotal, tax and total. Quote and invoice pages
 * both used to carry their own copy of this inside a 500-line list view.
 */
import type { LineItem } from '../components/ui/LineItemsEditor';

export interface LineItemInput {
  description: string;
  quantity: number;
  unit_price: number;
}

export interface SavedLineItem {
  id?: string;
  description: string;
  quantity: number;
  unit_price: number;
}

let counter = 0;
const newKey = () => `li-${Date.now()}-${(counter += 1)}`;

export const blankLineItem = (): LineItem => ({
  id: newKey(),
  values: { description: '', quantity: '1', unit_price: '' },
});

/** Saved rows from the API, shaped for LineItemsEditor. An empty record starts with one blank row. */
export function toEditorItems(items: SavedLineItem[] | undefined): LineItem[] {
  if (!items || items.length === 0) return [blankLineItem()];
  return items.map(it => ({
    id: it.id ?? newKey(),
    values: {
      description: it.description,
      quantity: String(it.quantity),
      unit_price: String(it.unit_price),
    },
  }));
}

/** Rows ready to send: blank descriptions are dropped, numbers parsed. */
export function fromEditorItems(items: LineItem[]): LineItemInput[] {
  return items
    .filter(li => li.values.description.trim())
    .map(li => ({
      description: li.values.description.trim(),
      quantity: Number(li.values.quantity) || 0,
      unit_price: Number(li.values.unit_price) || 0,
    }));
}

export function computeTotals(items: LineItemInput[], taxRatePercent: number) {
  const subtotal = items.reduce((sum, it) => sum + it.quantity * it.unit_price, 0);
  const taxAmount = subtotal * (taxRatePercent / 100);
  return { subtotal, taxAmount, total: subtotal + taxAmount };
}

/**
 * An amount with its currency, or the bare number when no currency has been
 * chosen yet. A new invoice has no currency until the server stamps one, and
 * guessing a symbol there would show the wrong money.
 */
export function formatAmount(
  amount: number,
  currency: string | null | undefined,
  formatMoney: (amount: number, code?: string | null) => string,
): string {
  return currency ? formatMoney(amount, currency) : Math.round(amount).toLocaleString();
}
